import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';

import { useCustomStore } from '../../hooks';
import { InputText } from '../Forms/InputText';

const InfiniteSelect = styled('div')(() => ({
  position: 'relative',
}));

const Container = styled(Box)(() => ({
  backgroundColor: '#FFF',
  boxShadow: '2px 1px 5px 1px #cccccc',
  maxHeight: '150px',
  overflowY: 'scroll',
  position: 'absolute',
  width: '100%',
  zIndex: 10,
}));

const SelectEquipmentFromAll: React.FC<{
  errors?: string;
  name?: string;
  onChange: (value: string | null) => unknown;
  disable?: boolean;
  disableNoneOption?: boolean;
  value: string;
  _ref: any;
  initialVin?: string;
  type?: 'power unit' | 'trailer';
  _key?: string;
}> = ({
  errors,
  onChange,
  name,
  value,
  disable = false,
  disableNoneOption = false,
  _ref,
  initialVin,
  type,
  _key = '',
}) => {
  const { inspectionsAndViolationsStore } = useCustomStore();
  const [showList, setShowList] = useState<boolean>();
  const [vin, setVin] = useState<string>();
  const [equipmentList, setEquipmentList] = useState<any>([]);
  const [nextLink, setNextLink] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>();

  const intervalRef = useRef<NodeJS.Timeout>();

  const [isPointerAway, setIsPointerAway] = useState<boolean>(false);

  React.useEffect(() => {
    if (equipmentList.length) return;
    setShowLoader(true);
    inspectionsAndViolationsStore.getAllEquipments({ type }).then((resp) => {
      if (resp?.list.length) {
        setEquipmentList([...equipmentList, ...resp.list]);
      }
      setNextLink(resp.nextLink);
      setShowLoader(false);
    });
  }, [
    inspectionsAndViolationsStore,
    type,
    equipmentList,
    equipmentList.length,
  ]);

  const handleScroll = (event: any) => {
    const myDiv = event?.target as HTMLDivElement;
    if (myDiv.offsetHeight + myDiv.scrollTop >= myDiv.scrollHeight - 100) {
      if (nextLink) {
        setShowLoader(true);
        inspectionsAndViolationsStore
          .getAllEquipments({
            nextLink,
            type,
          })
          .then((resp) => {
            setEquipmentList([...equipmentList, ...resp.list]);
            setNextLink(resp.nextLink);
            setShowLoader(false);
          });
      }
    }
  };

  const handleTextChange = (text: string) => {
    onChange('');
    setVin(text);
    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = setTimeout(() => {
      setShowLoader(true);
      inspectionsAndViolationsStore
        .getAllEquipments({
          type,
          vin: text,
        })
        .then((resp) => {
          setEquipmentList([...resp.list]);
          setNextLink(resp.nextLink);
          setShowLoader(false);
        });
    }, 500);
  };

  useEffect(() => {
    const fetch = async () => {
      if (!value) {
        setVin('');
        return;
      }
      if (vin) return;
      let selectedEquipment: any = {};
      if (equipmentList.length > 0) {
        selectedEquipment = equipmentList.find((el: any) => el?.id === value);
        if (Object.keys(selectedEquipment).length === 0) {
          selectedEquipment = await getEquipmentFromAPI(value);
        }
      } else {
        selectedEquipment = await getEquipmentFromAPI(value);
      }
      setVin(selectedEquipment?.vin || initialVin);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getEquipmentFromAPI = async (value: string) => {
    let equipments = [];
    const resp = await inspectionsAndViolationsStore.getAllEquipments({
      type,
      unitId: value,
    });
    const list = resp.list;
    equipments = [...list];
    const sd = equipments.find((el: any) => el?.id === value);
    return sd;
  };

  return (
    <InfiniteSelect id="infinite-select-container" key={_key}>
      <InputText
        autoComplete="off"
        inputRefObj={_ref}
        errors={errors}
        name={name as string}
        isRefreshValue={true}
        value={vin}
        onClick={() => setShowList(true)}
        onChangeText={handleTextChange}
        isEditable={disable}
        type="text"
        variant="standard"
        placeholder="Search"
        onBlurText={() => {
          if (isPointerAway) {
            setShowList(false);
          }
        }}
      />

      {showList && !disable && (
        <Container
          onMouseEnter={() => setIsPointerAway(false)}
          onMouseLeave={() => setIsPointerAway(true)}
          onScroll={handleScroll}>
          <List>
            {!showLoader && equipmentList.length === 0 && (
              <ListItem>
                <ListItemButton>
                  <ListItemText primary="-No Results-" />
                </ListItemButton>
              </ListItem>
            )}
            {equipmentList.map((equipment: any, index: number) => (
              <ListItem
                disablePadding
                key={index}
                onClick={() => {
                  onChange(equipment.id);
                  setVin(equipment.vin);
                  setShowList(false);
                }}>
                <ListItemButton>
                  <ListItemText primary={equipment.vin} />
                </ListItemButton>
              </ListItem>
            ))}
            {showLoader && (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <CircularProgress
                  size={30}
                  sx={{ color: '#DEC330', marginTop: 20 }}
                />
              </div>
            )}
          </List>
        </Container>
      )}
    </InfiniteSelect>
  );
};

export default observer(SelectEquipmentFromAll);
